import { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSettings } from '@/lib/store';
import { usePlayer } from '@/features/player/audioEngine';
import { useResolvedTheme } from '@/features/settings/theme';
import { chapter, cleanTranslation, versesByPage, wordLine, wordPage, type PageLayout, type Verse, type Word } from './api';
import { glyphOf, isGlyphMushaf, layoutOf, loadPageFont, pageFontStyle } from './fonts';
import { BASMALAH_INDOPAK, BASMALAH_QPC, indopakVerse, indopakWords, joinedWords, qpcWord } from './script';
import { alignTajweedWords } from './tajweedAlign';
import { TajweedWords } from './Tajweed';

export function useHighlight() {
  return usePlayer((s) => s.current);
}

/** The āyāt on a page of the chosen muṣḥaf's print (shared by the reader and the page). */
export function usePageVerses(page: number, enabled = true) {
  const { mushaf, translationId } = useSettings((s) => s.settings);
  const layout = layoutOf(mushaf);
  return useQuery({ queryKey: ['page', layout, page, translationId], queryFn: () => versesByPage(page, translationId, layout), staleTime: Infinity, enabled });
}

function SurahBanner({ surah }: { surah: number }) {
  const ch = chapter(surah);
  return (
    <div className="my-1 flex items-center justify-center gap-3 rounded-xl border border-gold/40 bg-gold/5 py-1.5" dir="rtl">
      <span className="text-[11px] font-semibold text-muted" dir="ltr">{ch.v} āyāt</span>
      <span className="font-arabic text-[1.05em] text-gold">سُورَةُ {ch.ar}</span>
      <span className="text-[11px] font-semibold text-muted" dir="ltr">{ch.place === 'M' ? 'Makki' : 'Madani'}</span>
    </div>
  );
}

/** Words of an āyah that are words (not the āyah-number marker). */
const wordsOf = (v: Verse) => v.words.filter((w) => w.char_type_name === 'word');

/** Per-word tajweed markup on the QPC text (null without tajweed data). */
function useTajweedWords(v: Verse, on: boolean) {
  return useMemo(() => (on && v.text_uthmani_tajweed ? alignTajweedWords(wordsOf(v).map(qpcWord), v.text_uthmani_tajweed) : null), [v, on]);
}

/* ── Page view (fixed 15-line Madinah layout) ────────────────────────── */

interface LineItem { line: number; words: (Word & { verseKey: string; joinNext: boolean })[] }

function buildLines(verses: Verse[], layout: PageLayout): { lines: LineItem[]; headers: Map<number, number> } {
  const map = new Map<number, LineItem>();
  for (const v of verses) {
    const joined = joinedWords(v);
    for (const w of v.words) {
      const n = wordLine(w, layout);
      const l = map.get(n) ?? { line: n, words: [] };
      l.words.push({ ...w, verseKey: v.verse_key, joinNext: joined.has(w.position) });
      map.set(n, l);
    }
  }
  const lines = [...map.values()].sort((a, b) => a.line - b.line);
  /* Empty line slots before a surah's first ayah hold its name banner and the
     basmala — the API omits them, so reconstruct from the gaps. */
  const headers = new Map<number, number>(); // line number → surah
  for (const v of verses) {
    if (v.verse_number !== 1) continue;
    const first = v.words[0] ? wordLine(v.words[0], layout) : 1;
    const s = Number(v.verse_key.split(':')[0]);
    const slots = chapter(s).bis && s !== 1 && s !== 9 ? 2 : 1;
    headers.set(first - slots, s);
  }
  return { lines, headers };
}

export const MushafPage = memo(function MushafPage({ page, onWord }: { page: number; onWord: (verseKey: string, word?: number) => void }) {
  const mushaf = useSettings((s) => s.settings.mushaf);
  const theme = useResolvedTheme();
  const layout = layoutOf(mushaf);
  const { data: verses, error } = usePageVerses(page);
  const [fontReady, setFontReady] = useState(false);
  const hl = useHighlight();

  useEffect(() => {
    setFontReady(!isGlyphMushaf(mushaf));
    let alive = true;
    loadPageFont(mushaf, page, theme).then(() => alive && setFontReady(true)).catch(() => alive && setFontReady(true));
    // Warm the neighbours so swiping is instant.
    [page - 1, page + 1].filter((p) => p >= 1 && p <= 604).forEach((p) => loadPageFont(mushaf, p, theme).catch(() => {}));
    return () => { alive = false; };
  }, [mushaf, page, theme]);

  const built = useMemo(() => (verses ? buildLines(verses, layout) : null), [verses, layout]);

  if (error) return <div className="mushaf-page grid aspect-[0.66] place-items-center p-6 text-center text-muted">Couldn't load page {page}. You're offline and it hasn't been downloaded yet.</div>;
  if (!verses || !built || !fontReady) return <div className="mushaf-page aspect-[0.66] animate-pulse" aria-busy />;

  if (!isGlyphMushaf(mushaf)) return <FlowPage verses={verses} onWord={onWord} />;

  const fontStyle = pageFontStyle(mushaf, page, theme);
  const centeredPage = page <= 2;
  const rows: React.ReactNode[] = [];
  const maxLine = Math.max(15, ...built.lines.map((l) => l.line));
  let li = 0;
  for (let n = 1; n <= maxLine; n++) {
    const line = built.lines[li]?.line === n ? built.lines[li++] : null;
    if (!line) {
      const s = built.headers.get(n);
      const bis = built.headers.get(n - 1);
      if (s) rows.push(<SurahBanner key={`h${n}`} surah={s} />);
      else if (bis) rows.push(<div key={`b${n}`} className="mushaf-line centered text-[0.82em] !leading-[2.4] [font-family:UthmanicHafs]" dir="rtl">{BASMALAH_QPC}</div>);
      else if (n <= 15 && !centeredPage) rows.push(<div key={`e${n}`} className="mushaf-line" aria-hidden>&nbsp;</div>);
      continue;
    }
    const centered = centeredPage || line.words.length <= 3;
    // Words the muṣḥaf writes as one (لَّوۡمَا) stay together — the line's justification spreads the rest.
    const groups: LineItem['words'][] = [];
    line.words.forEach((w, i) => { if (i && line.words[i - 1].joinNext) groups[groups.length - 1].push(w); else groups.push([w]); });
    rows.push(
      <div key={n} className={`mushaf-line ${centered ? 'centered' : ''}`}>
        {groups.map((g) => (
          <span key={g[0].id}>
            {g.map((w) => {
              const ayahOn = hl.verseKey === w.verseKey;
              const wordOn = ayahOn && hl.word === w.position && w.char_type_name === 'word';
              return (
                <span key={w.id} className={`qword ${ayahOn ? 'ayah-on' : ''} ${wordOn ? 'word-on' : ''}`} data-key={w.verseKey}
                  onClick={() => onWord(w.verseKey, w.char_type_name === 'word' ? w.position : undefined)}>
                  {glyphOf(mushaf, w)}
                </span>
              );
            })}
          </span>
        ))}
      </div>,
    );
  }

  return (
    <div className="mushaf-page px-[4%] py-[5%] [container-type:inline-size]">
      <div style={{ ...fontStyle, fontSize: 'min(5.9cqw, 38px)' }} className={centeredPage ? 'flex min-h-[60%] flex-col justify-center' : ''}>
        {rows}
      </div>
      <PageFooter verses={verses} page={page} />
    </div>
  );
});

function PageFooter({ verses, page }: { verses: Verse[]; page: number }) {
  const first = verses[0];
  return (
    <div className="mt-3 flex items-center justify-between text-[12px] font-medium text-muted">
      <span>Juz {first?.juz_number}</span>
      <span className="tabular-nums">{page}</span>
      <span>{chapter(Number(first?.verse_key.split(':')[0] ?? 1)).n}</span>
    </div>
  );
}

/* ── One āyah's words in a reflowable script (Uthmani, IndoPak, tajweed) ─ */

/** The āyah number — drawn by the app in every script, so it always matches the āyah key. */
function AyahNumber({ v, ayahOn = false, onWord }: { v: Verse; ayahOn?: boolean; onWord?: (k: string) => void }) {
  const end = v.words.find((w) => w.char_type_name === 'end');
  return <span className={`qword ayah-num ${ayahOn ? 'ayah-on' : ''}`} onClick={() => onWord?.(v.verse_key)}>{end?.text_uthmani ?? v.verse_number.toLocaleString('ar-EG')} </span>;
}

function FlowWords({ v, onWord, ayahOn, activeWord }: { v: Verse; onWord: (k: string, w?: number) => void; ayahOn: boolean; activeWord: number | null }) {
  const mushaf = useSettings((s) => s.settings.mushaf);
  const tajweed = useTajweedWords(v, mushaf === 'tajweed-text');
  const indopak = useMemo(() => (mushaf === 'indopak' ? indopakWords(v) : null), [v, mushaf]);
  const words = wordsOf(v);
  const joined = useMemo(() => joinedWords(v), [v]);
  const number = <AyahNumber v={v} ayahOn={ayahOn} onWord={onWord} />;
  if (tajweed) return <><TajweedWords words={tajweed.words} joined={joined} ayahOn={ayahOn} activeWord={activeWord} onWord={(pos) => onWord(v.verse_key, pos)} />{number}</>;
  // IndoPak divides a few āyāt into words differently — those highlight as a whole āyah.
  if (mushaf === 'indopak' && !indopak) return <><span className={`qword ${ayahOn ? 'ayah-on' : ''}`} onClick={() => onWord(v.verse_key)}>{indopakVerse(v) ?? words.map(qpcWord).join(' ')} </span>{number}</>;
  return (
    <>
      {words.map((w, i) => (
        <span key={w.id} className={`qword ${ayahOn ? 'ayah-on' : ''} ${activeWord === w.position ? 'word-on' : ''}`} onClick={() => onWord(v.verse_key, w.position)}>
          {indopak ? indopak[i] : qpcWord(w)}{!indopak && joined.has(w.position) ? '' : ' '}
        </span>
      ))}
      {number}
    </>
  );
}

/* ── Reflowable page (Uthmani / IndoPak scripts) ─────────────────────── */
function FlowPage({ verses, onWord }: { verses: Verse[]; onWord: (k: string, w?: number) => void }) {
  const { mushaf, arabicSize } = useSettings((s) => s.settings);
  const hl = useHighlight();
  const indopak = mushaf === 'indopak';
  return (
    <div className="mushaf-page px-5 py-6">
      <div className={`ayah-text ${indopak ? 'indopak' : 'uthmani'}`} style={{ fontSize: `${1.75 * arabicSize}rem` }}>
        {verses.map((v) => {
          const s = Number(v.verse_key.split(':')[0]);
          const ayahOn = hl.verseKey === v.verse_key;
          return (
            <span key={v.id}>
              {v.verse_number === 1 && <><SurahBanner surah={s} />{chapter(s).bis && s !== 1 && s !== 9 && <div className="text-center">{indopak && v.text_indopak_nastaleeq ? BASMALAH_INDOPAK : BASMALAH_QPC}</div>}</>}
              <FlowWords v={v} onWord={onWord} ayahOn={ayahOn} activeWord={ayahOn ? hl.word : null} />
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ── Ayah list (translation / transliteration / word-by-word) ────────── */
export const AyahRow = memo(function AyahRow({ verse, onWord, onMore }: { verse: Verse; onWord: (k: string, w?: number) => void; onMore: (k: string) => void }) {
  const { mushaf, arabicSize, showTranslation, showTransliteration, wordByWord } = useSettings((s) => s.settings);
  const theme = useResolvedTheme();
  const hl = useHighlight();
  const glyph = isGlyphMushaf(mushaf);
  const layout = layoutOf(mushaf);
  const [ready, setReady] = useState(!glyph);
  // Each word's glyph is in the page font of its own page, in that print.
  const pagesNeeded = useMemo(() => [...new Set(verse.words.map((w) => wordPage(w, layout)))], [verse, layout]);
  const tajweed = useTajweedWords(verse, mushaf === 'tajweed-text');
  const indopak = useMemo(() => (mushaf === 'indopak' ? indopakWords(verse) : null), [verse, mushaf]);
  const joined = useMemo(() => joinedWords(verse), [verse]);

  useEffect(() => {
    if (!glyph) { setReady(true); return; }
    let alive = true;
    Promise.all(pagesNeeded.map((p) => loadPageFont(mushaf, p, theme))).then(() => alive && setReady(true), () => alive && setReady(true));
    return () => { alive = false; };
  }, [glyph, mushaf, pagesNeeded, theme]);

  const ayahOn = hl.verseKey === verse.verse_key;
  const translation = verse.translations?.[0]?.text;
  const script = mushaf === 'indopak' && verse.text_indopak_nastaleeq ? 'indopak' : 'uthmani';
  return (
    <article id={`v-${verse.verse_key}`} className={`border-b border-hairline px-4 py-5 transition-colors ${ayahOn ? 'bg-[var(--highlight)]' : ''}`}>
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => onMore(verse.verse_key)} className="press rounded-full bg-fill px-3 py-1 text-[13px] font-semibold text-muted">{verse.verse_key}</button>
      </div>
      {tajweed ? (
        <div className="ayah-text uthmani" dir="rtl" style={{ fontSize: `${1.85 * arabicSize}rem` }}>
          <TajweedWords words={tajweed.words} joined={joined} ayahOn={false} activeWord={ayahOn ? hl.word : null} onWord={(pos) => onWord(verse.verse_key, pos)} />
          <AyahNumber v={verse} onWord={onWord} />
        </div>
      ) : mushaf === 'indopak' && !indopak ? (
        <div className={`ayah-text ${script}`} dir="rtl" style={{ fontSize: `${1.75 * arabicSize}rem` }}>
          <span className="qword" onClick={() => onWord(verse.verse_key)}>{indopakVerse(verse) ?? wordsOf(verse).map(qpcWord).join(' ')} </span>
          <AyahNumber v={verse} onWord={onWord} />
        </div>
      ) : <div className={`ayah-text ${glyph ? '' : script} flex flex-wrap gap-x-[0.28em]`} dir="rtl"
        style={{ fontSize: `${(glyph ? 1.9 : 1.75) * arabicSize}rem`, visibility: ready ? 'visible' : 'hidden' }}>
        {verse.words.map((w) => {
          const wordOn = ayahOn && hl.word === w.position && w.char_type_name === 'word';
          const end = w.char_type_name === 'end';
          const text = glyph ? glyphOf(mushaf, w) : end ? w.text_uthmani : indopak ? indopak[w.position - 1] : qpcWord(w);
          return (
            <span key={w.id} className={`qword inline-flex flex-col items-center ${wordOn ? 'word-on' : ''} ${end && !glyph ? 'ayah-num' : ''}`}
              // A word written joined to the next (لَّوۡمَا) cancels the gap between them.
              style={{ ...(glyph ? pageFontStyle(mushaf, wordPage(w, layout), theme) : null), ...(!indopak && joined.has(w.position) ? { marginInlineEnd: '-0.28em' } : null) }}
              onClick={() => onWord(verse.verse_key, end ? undefined : w.position)}>
              <span>{text}</span>
              {wordByWord && !end && (
                <span className="font-sans text-[11px] leading-tight text-muted" dir="ltr">{w.translation?.text}</span>
              )}
            </span>
          );
        })}
      </div>}
      {showTransliteration && (
        <p className="mt-3 text-[15px] italic leading-relaxed text-muted">{wordsOf(verse).map((w) => w.transliteration?.text).join(' ')}</p>
      )}
      {showTranslation && translation && <p className="mt-3 text-[16px] leading-relaxed">{cleanTranslation(translation)}</p>}
    </article>
  );
});
